import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PASSWORD_MIN, PASSWORD_MAX, passwordChecks, allValid, firstError } from "@/lib/passwordPolicy";

const SYMBOL = `${process.env.PUBLIC_URL}/amanah-symbol.png`;

function ChecklistItem({ ok, children, testid }) {
  return (
    <li className="flex items-center gap-2 text-sm" data-testid={testid}>
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full ${
          ok ? "bg-brand-green text-white" : "border border-line text-transparent"
        }`}
      >
        <Check className="h-3 w-3" />
      </span>
      <span className={ok ? "text-brand-greenDeep" : "text-slate-muted"}>{children}</span>
    </li>
  );
}

export default function ResetPasswordPage() {
  usePageMeta("Set a new password — Amanah Promotions");
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setStatus("invalid");
        return;
      }
      try {
        const { data } = await api.post("/auth/reset-password/verify", { token });
        if (active) setStatus(data.valid ? "valid" : "invalid");
      } catch {
        if (active) setStatus("invalid");
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const checks = passwordChecks(password, confirm);
  const canSubmit = allValid(password, confirm) && !saving;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allValid(password, confirm)) {
      setError(firstError(password, confirm));
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password, confirm_password: confirm });
      setStatus("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to reset your password. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 py-16">
        <Link to="/" className="mb-10 flex items-center gap-3" data-testid="reset-home-link">
          <img src={SYMBOL} alt="Amanah" className="h-10 w-auto" />
          <span className="font-display text-lg font-medium text-navy">Amanah Promotions</span>
        </Link>

        <div className="border border-line bg-white p-8 sm:p-10">
          {status === "checking" && (
            <p className="text-sm text-slate-muted" data-testid="reset-checking">Checking your reset link…</p>
          )}

          {status === "invalid" && (
            <div data-testid="reset-invalid">
              <AlertTriangle className="h-9 w-9 text-red-600" />
              <h1 className="mt-4 font-display text-2xl font-medium text-navy">Link invalid or expired</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-body">
                This password reset link is invalid or has expired. Reset links last 30 minutes and can
                be used once. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-green hover:text-brand-greenDeep"
                data-testid="reset-request-new"
              >
                Request a new link
              </Link>
            </div>
          )}

          {status === "done" && (
            <div data-testid="reset-success">
              <CheckCircle2 className="h-9 w-9 text-brand-green" />
              <h1 className="mt-4 font-display text-2xl font-medium text-navy">Password updated</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-body">
                Your password has been changed and you&apos;ve been signed out of any existing sessions. You
                can now sign in with your new password.
              </p>
              <Link
                to="/site-settings/login"
                className="mt-6 inline-flex items-center gap-2 bg-brand-green px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-greenDeep"
                data-testid="reset-go-login"
              >
                Go to sign in <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {status === "valid" && (
            <>
              <h1 className="font-display text-2xl font-medium text-navy sm:text-3xl">Set a new password</h1>

              <div className="mt-6 border-l-2 border-brand-green bg-cream p-4 text-sm leading-relaxed text-slate-body">
                <p className="font-semibold text-navy">Password requirements</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  <li>Use between {PASSWORD_MIN} and {PASSWORD_MAX} characters.</li>
                  <li>Capital letters, numbers and symbols are optional.</li>
                  <li>A long, memorable passphrase is acceptable.</li>
                  <li>Your two password entries must match.</li>
                </ul>
              </div>

              <form onSubmit={onSubmit} className="mt-6 space-y-5" noValidate>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="reset-password" className="block text-sm font-medium text-navy">
                      New password
                    </label>
                    <span className="text-xs text-slate-muted" data-testid="reset-counter">
                      {password.length}/{PASSWORD_MAX}
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      id="reset-password"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 rounded-none border-line bg-white pr-11 text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                      autoComplete="new-password"
                      data-testid="reset-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-muted hover:text-navy"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      data-testid="reset-toggle-password"
                    >
                      {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="reset-confirm" className="mb-2 block text-sm font-medium text-navy">
                    Confirm new password
                  </label>
                  <div className="relative">
                    <Input
                      id="reset-confirm"
                      type={showConfirm ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="h-12 rounded-none border-line bg-white pr-11 text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                      autoComplete="new-password"
                      data-testid="reset-confirm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-muted hover:text-navy"
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                      data-testid="reset-toggle-confirm"
                    >
                      {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <ul className="space-y-2">
                  <ChecklistItem ok={checks.length} testid="reset-check-length">
                    {PASSWORD_MIN}–{PASSWORD_MAX} characters
                  </ChecklistItem>
                  <ChecklistItem ok={checks.notCommon} testid="reset-check-common">
                    Not a commonly used password
                  </ChecklistItem>
                  <ChecklistItem ok={checks.match} testid="reset-check-match">
                    Passwords match
                  </ChecklistItem>
                </ul>

                {error && (
                  <p role="alert" className="text-sm font-medium text-red-600" data-testid="reset-error">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="inline-flex w-full items-center justify-center gap-2 bg-brand-green px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-greenDeep disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="reset-submit"
                >
                  {saving ? "Updating…" : "Update password"}
                  {!saving && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}
        </div>

        <Link to="/site-settings/login" className="mt-8 text-sm text-slate-muted hover:text-brand-green">
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
}
